/**
 * Relabel a breakdown column's values: `True` / `False` under "OnCourtDefense in [player]" can read "w/ Donovan" /
 * "w/o Donovan". Display only (table state `vl`): sorting, filtering, pins and the query keep the stored values, so a
 * label can never break a saved view. Opened from the column menu.
 */
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";

/** More values than this and a column is a list of names, not something to relabel by hand. */
export const MAX_RELABEL_VALUES = 60;

interface Props {
  title: string;
  values: string[];
  initial: Record<string, string>;
  onSave: (labels: Record<string, string>) => void;
  onCancel: () => void;
}

export default function ValueLabelsDialog({ title, values, initial, onSave, onCancel }: Props) {
  const [labels, setLabels] = useState<Record<string, string>>(initial);
  const shown = values.slice(0, MAX_RELABEL_VALUES);

  const save = () => {
    const cleaned: Record<string, string> = {};
    for (const [raw, label] of Object.entries(labels)) {
      const trimmed = label.trim();
      if (trimmed && trimmed !== raw) cleaned[raw] = trimmed;
    }
    onSave(cleaned);
  };

  return (
    <Dialog open onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: "0.95rem", fontWeight: 700, pb: 0.5 }}>
        Relabel values · {title}
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", mb: 1 }}>
          Changes what the cells show. Sorting, filters and pins keep the original values.
        </Typography>
        {shown.map((raw) => (
          <Box key={raw} display="flex" alignItems="center" gap={1} py={0.4}>
            <Typography
              title={raw}
              sx={{
                fontSize: "0.8rem",
                color: "text.secondary",
                width: "40%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {raw}
            </Typography>
            <TextField
              size="small"
              fullWidth
              placeholder={raw}
              value={labels[raw] ?? ""}
              onChange={(e) => setLabels((prev) => ({ ...prev, [raw]: e.target.value }))}
              onKeyDown={(e) => {
                if (e.key === "Enter") save();
              }}
              inputProps={{ "aria-label": `Label for ${raw}`, style: { fontSize: "0.8rem" } }}
            />
          </Box>
        ))}
        {values.length > MAX_RELABEL_VALUES && (
          <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", mt: 1 }}>
            Showing the first {MAX_RELABEL_VALUES} of {values.length} values.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button size="small" onClick={() => setLabels({})} sx={{ textTransform: "none", mr: "auto" }}>
          Clear all
        </Button>
        <Button size="small" onClick={onCancel} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button size="small" variant="contained" onClick={save} sx={{ textTransform: "none" }}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
